import { useCallback, useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";

import FormSaveButton from "components/Forms/SaveButton";
import ReadMoreDocsLink from "components/ReadMoreDocsLink";
import SpaceSelector from "components/SpaceSelector";
import SharedModulesTierInfo from "components/TierInfo/SharedModulesTierInfo";
import { Magnify } from "components/icons/generated";
import { SEARCH_PLACEHOLDERS } from "constants/searchPlaceholders";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import Icon from "ds/components/Icon";
import Input from "ds/components/Input";
import PageInfoHeaderActions from "ds/components/PageInfoHeader/Actions";
import PageInfoHeaderTitle from "ds/components/PageInfoHeader/Title";
import PageInfoHeaderWrapper from "ds/components/PageInfoHeader/Wrapper";
import TooltipInfo from "ds/components/TooltipInfo";
import TooltipModalBody from "ds/components/TooltipModal/Body";
import { TooltipModalTitle } from "ds/components/TooltipModal/Title";
import Typography from "ds/components/Typography";
import useBlockNavigationModal from "hooks/useBlockNavigationModal";
import useTypedContext from "hooks/useTypedContext";
import useAllowedSpaceToShareModule from "shared/Module/access/useAllowedSpaceToShareModule";
import { getDocsUrl } from "utils/getDocsUrl";
import { useShareModule } from "views/Account/NewModule/ShareNew/useShareModule";

import { ModuleSettingsContext } from "../Context";
import styles from "./styles.module.css";
import { getDefaultValues } from "./utils";

type FormValues = {
  sharedAccounts: Array<{ value: string }>;
  sharedSpaces: Array<{ value: string }>;
};

const ModuleSettingsSharingEditForm = () => {
  const spaces = useAllowedSpaceToShareModule();
  const { module } = useTypedContext(ModuleSettingsContext);
  const { shareModule, loading } = useShareModule({
    moduleId: module.id,
  });
  const [searchQuery, setSearchQuery] = useState("");

  const builderForm = useForm<FormValues>({
    defaultValues: getDefaultValues(module),
    mode: "onChange",
  });

  const {
    handleSubmit,
    formState: { isDirty, isSubmitSuccessful },
    reset,
    watch,
    setValue,
  } = builderForm;

  const spaceFields = watch("sharedSpaces");

  useBlockNavigationModal({ isNavigationBlocked: isDirty });

  const selectedSpaceIds = useMemo(
    () => new Set(spaceFields?.map((space) => space.value).filter((id) => id)),
    [spaceFields]
  );

  const onSelectionChange = useCallback(
    (data: Set<string>) => {
      setValue(
        "sharedSpaces",
        [...data].map((id) => ({ value: id })),
        { shouldDirty: true }
      );
    },
    [setValue]
  );

  const onSubmit = useCallback(
    () =>
      handleSubmit((values: FormValues) => {
        return shareModule(values);
      })(),
    [shareModule, handleSubmit]
  );

  useEffect(() => {
    if (isSubmitSuccessful) {
      reset(getDefaultValues(module));
    }
  }, [isSubmitSuccessful, module, reset]);

  return (
    <FormProvider {...builderForm}>
      <Box direction="column" grow="1">
        <PageInfoHeaderWrapper align="center">
          <Box direction="row" gap="medium">
            <PageInfoHeaderTitle title="Availability" />

            <TooltipInfo variant="modal">
              <TooltipModalTitle>Availability</TooltipModalTitle>
              <TooltipModalBody align="start">
                <Typography tag="p" variant="p-body3">
                  Your module is automatically available in its origin space and all descendants.
                  Expand availability to other spaces as needed.
                </Typography>

                <ReadMoreDocsLink
                  docsUrl={getDocsUrl(
                    "/vendors/terraform/module-registry#define-module-availability"
                  )}
                />
              </TooltipModalBody>
            </TooltipInfo>
          </Box>
          <PageInfoHeaderActions>
            <Box className={styles.searchInputWrapper}>
              <Input
                type="text"
                placeholder={SEARCH_PLACEHOLDERS.SPACES}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={styles.searchInput}
              />
              <Icon className={styles.searchIcon} src={Magnify} />
            </Box>
            {isDirty && (
              <Button variant="secondary" onPress={() => reset(getDefaultValues(module))}>
                Cancel
              </Button>
            )}
            <FormSaveButton onPress={onSubmit} isDirty={isDirty} loading={loading} />
          </PageInfoHeaderActions>
        </PageInfoHeaderWrapper>
        <SharedModulesTierInfo type="callout" />
        <Box direction="column" grow="1" className={styles.tableWrapper}>
          <SpaceSelector
            spaces={spaces}
            selectedSpaceIds={selectedSpaceIds}
            onSelectionChange={onSelectionChange}
            defaultSelection={module.spaceDetails.id}
            showAvailabilitySummary={false}
            showSearch={false}
            searchPhrase={searchQuery}
            tableLayout
          />
        </Box>
      </Box>
    </FormProvider>
  );
};

export default ModuleSettingsSharingEditForm;
